import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '@/shared/auth/AuthContext';
import type { Role } from '@/shared/lib/auth/types';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';

interface RequireAuthProps {
  children: ReactNode;
  /** If provided, only these roles are allowed (others see "Access denied"). */
  roles?: Role[];
  /** Allow `mobile` role through this guard (used for /mobile-only). */
  allowMobile?: boolean;
}

function FullPageSkeleton(): JSX.Element {
  return (
    <div className="flex min-h-[100dvh] w-full bg-background">
      <div className="hidden md:flex w-64 flex-col border-r bg-card p-4 gap-3">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-2 mt-4">
          {Array.from({ length: 8 }).map((_, idx) => (
            <Skeleton key={idx} className="h-9 w-full" />
          ))}
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="flex h-16 items-center justify-between border-b bg-card px-6">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="size-8 rounded-full" />
        </div>
        <div className="flex-1 p-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    </div>
  );
}

function AccessDenied(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">{t('common.accessDenied')}</h1>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('common.accessDeniedBody')}</p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/">{t('common.backHome')}</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function RequireAuth({ children, roles, allowMobile = false }: RequireAuthProps): JSX.Element {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <FullPageSkeleton />;
  }

  if (status === 'mfa_challenge') {
    return <Navigate to="/mfa" replace />;
  }

  if (status === 'unauthenticated' || !user) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (user.role === 'mobile' && !allowMobile) {
    return <Navigate to="/mobile-only" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}
